import { placeAgentCreditAllowanceBanner } from '@heidi/commerce';
import type { ReactNode } from 'react';

import { AgentCreditAllowanceInChatBanner } from './AgentCreditAllowanceBanner';
import { useAgentCreditAllowanceBannerState } from './useAgentCreditAllowanceBannerState';

/**
 * Work home never mounts the credit banner. In-thread: usage-nudges ON only
 * shows 100% above the composer; flag off docks 90% above the composer and
 * puts 100 under the transcript (50/75 never mount).
 */
export function useAgentCreditAllowanceInChatChrome(
  enabled: boolean,
  options?: { inThread?: boolean }
) {
  const {
    surface,
    hideFollowUpQuestions,
    sendDisabled,
    forceEvidenceOnlyOnSend,
    isUsageNudgesEnabled,
  } = useAgentCreditAllowanceBannerState();
  const placement = enabled
    ? placeAgentCreditAllowanceBanner({
        surface,
        inThread: options?.inThread ?? false,
        isUsageNudgesEnabled,
      })
    : null;
  const banner =
    placement != null ? (
      <AgentCreditAllowanceInChatBanner placement={placement} />
    ) : undefined;

  const inThread = options?.inThread ?? false;
  // Usage-nudges: landing keeps Send open under Evidence only (host greys it
  // if the clinician clears the pill). In-thread Heidi 2 still greys Send.
  const resolvedSendDisabled =
    enabled && (sendDisabled || (forceEvidenceOnlyOnSend && inThread));

  return {
    composerDockLeading: placement === 'composer' ? banner : undefined,
    transcriptTrailing: placement === 'transcript' ? banner : undefined,
    hideFollowUpQuestions: enabled && hideFollowUpQuestions,
    sendDisabled: resolvedSendDisabled,
    // Landing only: host defaults Evidence only on; clearing greys Work Send.
    forceEvidenceOnlyOnSend: enabled && forceEvidenceOnlyOnSend && !inThread,
  } satisfies {
    composerDockLeading: ReactNode;
    transcriptTrailing: ReactNode;
    hideFollowUpQuestions: boolean;
    sendDisabled: boolean;
    forceEvidenceOnlyOnSend: boolean;
  };
}
